import React from 'react';

const FocusAreas: React.FC = () => {
  const energyProjects = [
    {
      title: "Peaker Gas Power Plants",
      description: "Strategically developing flexible, rapid-response gas-fired power plants to ensure grid stability and meet peak demand fluctuations.",
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Hydroelectric Projects",
      description: "Leveraging Chile's abundant water resources through efficient and environmentally conscious run-of-the-river hydroelectric projects.",
      color: "from-cyan-500 to-blue-600"
    },
    {
      title: "Wind Projects",
      description: "Harnessing the significant wind potential, particularly in the southern regions, to contribute to a diversified and sustainable energy matrix.",
      color: "from-green-500 to-emerald-600"
    }
  ];

  const hpcProjects = [
    {
      title: "Strategic Northern Sites",
      description: "Advancing two strategic sites in northern Chile, leveraging the region's unparalleled low-cost solar energy resources.",
      color: "from-orange-500 to-red-600"
    },
    {
      title: "Curtailment Optimization",
      description: "Actively seeking facilities experiencing curtailment to deploy behind-the-meter, containerized ASIC units.",
      color: "from-purple-500 to-pink-600"
    },
    {
      title: "Integrated Infrastructure",
      description: "Integrating reliable energy supply with robust connectivity, including access to LNG terminals and gas pipelines.",
      color: "from-indigo-500 to-purple-600"
    }
  ];

  return (
    <section id="focus-areas" className="py-24 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-gray-900 dark:text-white">
            Our Solutions
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-16 max-w-3xl mx-auto">
            Comprehensive energy infrastructure and computing solutions tailored for Chile's unique landscape and opportunities
          </p>
          
          <div className="space-y-20">
            <div>
              <h3 className="text-3xl font-semibold mb-12 text-gray-900 dark:text-white">
                Energy Infrastructure
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                {energyProjects.map((project, index) => (
                  <div 
                    key={index} 
                    className="group relative bg-white dark:bg-gray-900 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300 shadow-lg dark:shadow-none"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
                    <div className="relative z-10">
                      <div className={`w-16 h-1 bg-gradient-to-r ${project.color} mb-6`}></div>
                      <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                        {project.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-3xl font-semibold mb-12 text-gray-900 dark:text-white">
                High-Performance Computing & Data Centers
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                {hpcProjects.map((project, index) => (
                  <div 
                    key={index} 
                    className="group relative bg-white dark:bg-gray-900 rounded-2xl p-8 hover:transform hover:scale-105 transition-all duration-300 shadow-lg dark:shadow-none"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${project.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
                    <div className="relative z-10">
                      <div className={`w-16 h-1 bg-gradient-to-r ${project.color} mb-6`}></div>
                      <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                        {project.title}
                      </h4>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="mt-20 relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-700"></div>
            <div className="relative z-10 p-16 text-center">
              <h3 className="text-3xl font-bold mb-6 text-white">
                Chile's Strategic Advantage
              </h3>
              <p className="text-xl text-gray-100 dark:text-gray-100 max-w-3xl mx-auto leading-relaxed">
                We firmly believe that the exponential growth of AI power factories will exert unprecedented pressure on global energy capacity. Chile stands poised to become a pivotal alternative with its robust infrastructure, fiber optic networks, exceptional solar radiation, and significant wind resources.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FocusAreas;